import { isValid, parse } from 'date-fns';
import { useIntl } from 'react-intl';

import { DateFormat, getReactIntlDateFormatOptions } from './utils';

/**
 * ICU-compliant date parsing hook that maintains consistency with useFormatDate
 * Uses native Intl.DateTimeFormat to derive parsing patterns from ICU locale data
 */
export const useICUDateParsing = () => {
  const intl = useIntl();

  /**
   * Generate ICU-compliant parsing patterns for a given locale and format
   */
  const getICUParsingPatterns = (
    locale: string,
    dateFormat: DateFormat,
  ): string[] => {
    const formatOptions = getReactIntlDateFormatOptions(dateFormat);

    // Use test dates to analyze ICU format structure
    const testSingle = new Date(2025, 0, 5); // Jan 5, 2025 (single digits)
    const testDouble = new Date(2025, 11, 25); // Dec 25, 2025 (double digits)

    const singleParts = new Intl.DateTimeFormat(
      locale,
      formatOptions,
    ).formatToParts(testSingle);
    const doubleParts = new Intl.DateTimeFormat(
      locale,
      formatOptions,
    ).formatToParts(testDouble);

    // Derive base pattern from ICU format parts
    let basePattern = '';
    let separator = '';

    singleParts.forEach((part) => {
      switch (part.type) {
        case 'year':
          basePattern += part.value.length === 4 ? 'yyyy' : 'yy';
          break;
        case 'month':
          const doubleMonth = doubleParts.find((p) => p.type === 'month');
          basePattern += doubleMonth?.value.length === 2 ? 'MM' : 'M';
          break;
        case 'day':
          const doubleDay = doubleParts.find((p) => p.type === 'day');
          basePattern += doubleDay?.value.length === 2 ? 'dd' : 'd';
          break;
        case 'literal':
          if (!separator) separator = part.value;
          basePattern += part.value;
          break;
        default:
          break;
      }
    });

    // Generate ICU-compliant format variations
    const patterns: string[] = [basePattern];

    if (separator) {
      // Alternative separators (common user inputs)
      const altSeparators = ['.', '/', '-'].filter((sep) => sep !== separator);
      altSeparators.forEach((altSep) => {
        const altPattern = basePattern.replace(
          new RegExp(
            `\\${separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`,
            'g',
          ),
          altSep,
        );
        patterns.push(altPattern);
      });

      // Padding variations (dd/MM → d/M combinations)
      if (basePattern.includes('dd') || basePattern.includes('MM')) {
        const baseParts = basePattern.split(separator);
        if (baseParts.length === 3) {
          const [part1, part2, part3] = baseParts;

          // Create unpadded variations
          patterns.push(
            `${part1.replace(/dd|MM/g, (match) =>
              match === 'dd' ? 'd' : 'M',
            )}${separator}${part2}${separator}${part3}`,
            `${part1}${separator}${part2.replace(/dd|MM/g, (match) =>
              match === 'dd' ? 'd' : 'M',
            )}${separator}${part3}`,
            `${part1.replace(/dd|MM/g, (match) =>
              match === 'dd' ? 'd' : 'M',
            )}${separator}${part2.replace(/dd|MM/g, (match) =>
              match === 'dd' ? 'd' : 'M',
            )}${separator}${part3}`,
          );
        }
      }
    }

    // Add 2-digit year variations for common user input (e.g., 02/10/21)
    // These should come FIRST to prevent 4-digit patterns from misinterpreting 2-digit years
    const twoDigitPatterns: string[] = [];
    if (basePattern.includes('yyyy')) {
      const originalPatterns = [...patterns]; // Store original patterns
      originalPatterns.forEach((pattern) => {
        if (pattern.includes('yyyy')) {
          twoDigitPatterns.push(pattern.replace(/yyyy/g, 'yy'));
        }
      });

      // Reorder: Put 2-digit year patterns at the beginning for priority
      patterns.length = 0; // Clear patterns array
      patterns.push(...twoDigitPatterns, ...originalPatterns); // 2-digit first, then 4-digit
    }

    // Add ICU fallback patterns
    patterns.push('P'); // date-fns locale-aware format

    // Remove duplicates while preserving order
    return Array.from(new Set(patterns));
  };

  /**
   * Parse a date string using ICU-derived patterns
   * Maintains full ICU compliance by using patterns derived from Intl.DateTimeFormat
   */
  const parseICUDate = (
    dateString: string,
    format: DateFormat = 'short',
  ): Date | null => {
    if (!dateString?.trim()) return null;

    const locale = intl.locale || 'en';
    const patterns = getICUParsingPatterns(locale, format);

    // Try each ICU-derived pattern
    for (const pattern of patterns) {
      try {
        // Note: Not passing locale to date-fns since patterns are already locale-specific

        const parsedDate = parse(dateString.trim(), pattern, new Date());

        if (isValid(parsedDate)) {
          return parsedDate;
        }
      } catch {
        // Continue to next pattern
        continue;
      }
    }

    return null;
  };

  return { parseICUDate };
};
